import { type CopySettings } from '@/features/copySettings';
import { reformatHtml } from '@/helpers/markdownToHtml';
import {
  renderCanonicalHtmlWithSettings,
  transformCanonicalMarkdownWithSettings,
  type CopyFormattingLoss,
} from '@heidi/editor/core';

import {
  COPY_SUPERSEDED,
  isFormattingOff,
  writeHtmlAndMarkdownToClipboard,
  type ClipboardWriteOutcome,
} from './copyHelpers-v2';

// The named /core R3 pilot (task 8.2). The pure rendering half now lives in
// `@heidi/editor/core`; this path stays the app-facing entry point. Imported
// AND re-exported: `copyCanonicalNote` calls the transform below, and a bare
// re-export would not bind the name here.
export { renderCanonicalHtmlWithSettings, transformCanonicalMarkdownWithSettings };

type CopyCanonicalNoteArgs = {
  markdownContent: string;
  settings: CopySettings;
  /**
   * Measure formatting loss (two extra whole-note DOMParser parses, run
   * synchronously before the write). Defaults to false; opt in only when the
   * caller consumes the result.
   */
  includeFormattingLoss?: boolean;
};

/**
 * Per-toggle copy for markdown-native notes, like `copyWithSettingsV2` but
 * driven by stored markdown, not a live editor. Writes paste-friendly HTML
 * (`reformatHtml`) plus its markdown sibling; omits the HTML payload when every
 * formatting toggle is off (bullets are plain-text markers and don't count),
 * matching the plain-text contract.
 *
 * Resolves COPY_SUPERSEDED when a newer copy replaced this one before it was
 * published (polyfill queue coalescing, Safari generation check, desktop
 * plugin rescue). Otherwise a published attempt resolves the formatting-loss
 * metadata when `includeFormattingLoss` is set, and undefined when it is not.
 * A desktop-native RTF write that is superseded rejects with
 * `RtfClipboardError('CLIPBOARD_SUPERSEDED')` instead. Callers must treat both
 * forms as a silent cancellation: compare the resolved value to
 * COPY_SUPERSEDED, and catch the rejection with `isSupersededCopyError` (which
 * matches only the rejection).
 *
 * Stays app-side: it performs the clipboard write and reaches `reformatHtml`,
 * part of the legacy regex pipeline the kernel must not import.
 */
export function copyCanonicalNote(
  args: CopyCanonicalNoteArgs & { includeFormattingLoss: true }
): Promise<CopyFormattingLoss | typeof COPY_SUPERSEDED>;
export function copyCanonicalNote(
  args: CopyCanonicalNoteArgs & { includeFormattingLoss?: false }
): Promise<ClipboardWriteOutcome>;
export function copyCanonicalNote(
  args: CopyCanonicalNoteArgs
): Promise<CopyFormattingLoss | ClipboardWriteOutcome>;
export async function copyCanonicalNote({
  markdownContent,
  settings,
  includeFormattingLoss = false,
}: CopyCanonicalNoteArgs): Promise<CopyFormattingLoss | ClipboardWriteOutcome> {
  const { html, markdown, formattingLoss } = transformCanonicalMarkdownWithSettings({
    markdownContent,
    settings,
    includeFormattingLoss,
  });

  const outcome = await writeHtmlAndMarkdownToClipboard({
    ...(isFormattingOff(settings) ? {} : { html: reformatHtml(html) }),
    markdown,
    trackPublication: true,
  });
  return outcome === COPY_SUPERSEDED ? COPY_SUPERSEDED : formattingLoss;
}
